import { Link } from "react-router-dom";
import { Heart, Shield, ArrowRight, Lock, FileCheck, Users, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteConfig, fileUrl } from "@/lib/siteConfig";

/**
 * Smart link: uses cross-domain <a> when applyBase is set + path is /apply or /agent,
 * otherwise uses react-router <Link>.
 */
function ApplyLink({ to, applyBase, children, ...rest }) {
  if (applyBase) {
    const base = applyBase.replace(/\/$/, "");
    const url = `${base}${to}`;
    return <a href={url} {...rest}>{children}</a>;
  }
  return <Link to={to} {...rest}>{children}</Link>;
}

export default function Landing() {
  const { config } = useSiteConfig();
  const logoUrl = fileUrl(config.logo_file_id);
  const heroUrl = fileUrl(config.hero_media_file_id);
  const heroIsVideo = config.hero_media_type === "video";
  const applyBase = (config.apply_base_url || "").trim();
  const logoSizeMap = { small: "h-8", medium: "h-12", large: "h-16", xlarge: "h-24" };
  const headerLogoClass = logoSizeMap[config.logo_size] || "h-12";
  const headerHClass = config.logo_size === "xlarge" ? "h-28" : config.logo_size === "large" ? "h-20" : "h-16";

  return (
    <div className="min-h-screen bg-bone text-forest">
      {/* Promo banner */}
      {config.promo_enabled && config.promo_message && (
        <div className="bg-clay text-bone text-sm text-center py-2.5 px-4" data-testid="promo-banner">
          {config.promo_url ? (
            <a href={config.promo_url} className="hover:underline" data-testid="promo-banner-link">
              {config.promo_message} →
            </a>
          ) : (
            <span>{config.promo_message}</span>
          )}
        </div>
      )}

      {/* Header */}
      <header className="border-b border-line bg-bone sticky top-0 z-40">
        <div className={`max-w-7xl mx-auto px-6 lg:px-10 ${headerHClass} flex items-center justify-between`}>
          <div className="flex items-center gap-2" data-testid="brand-logo">
            {logoUrl ? (
              <img src={logoUrl} alt={config.brand_name} className={`${headerLogoClass} w-auto`} />
            ) : (
              <>
                <div className="w-8 h-8 bg-forest rounded-sm flex items-center justify-center">
                  <Shield className="w-4 h-4 text-bone" strokeWidth={2} />
                </div>
                <span className="font-heading font-medium text-lg tracking-tight">{config.brand_name}</span>
              </>
            )}
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-sage">
            <a href="#products" className="hover:text-forest transition-colors duration-200">Products</a>
            <a href="#why" className="hover:text-forest transition-colors duration-200">Why us</a>
            <a href="#contact" className="hover:text-forest transition-colors duration-200">Contact</a>
          </nav>
          <ApplyLink to="/agent/login" applyBase={applyBase} data-testid="agent-login-link">
            <Button
              variant="outline"
              className="border-forest text-forest hover:bg-forest hover:text-bone rounded-sm h-9"
            >
              Agent Login
            </Button>
          </ApplyLink>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-line">
        {heroUrl ? (
          <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 md:pt-14 pb-14">
            <div className="rounded-md overflow-hidden border border-line bg-cream" data-testid="hero-banner">
              {heroIsVideo ? (
                <video src={heroUrl} className="w-full h-auto block" autoPlay muted loop playsInline />
              ) : (
                <img src={heroUrl} alt="Hero" className="w-full h-auto block" />
              )}
            </div>
            <div className="mt-10 flex flex-col items-center text-center gap-6">
              <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold">
                <span className="inline-block w-6 h-px bg-clay align-middle mr-2" />
                {config.hero_eyebrow}
              </div>
              <p className="text-base md:text-lg text-sage max-w-2xl leading-relaxed">
                {config.hero_subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <ApplyLink to="/apply/health" applyBase={applyBase} data-testid="cta-apply-health">
                  <Button className="bg-forest hover:bg-forestdark text-bone h-12 px-7 rounded-sm font-medium w-full sm:w-auto">
                    {config.cta_health_label} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </ApplyLink>
                <ApplyLink to="/apply/life" applyBase={applyBase} data-testid="cta-apply-life">
                  <Button
                    variant="outline"
                    className="border-forest text-forest hover:bg-forest hover:text-bone h-12 px-7 rounded-sm font-medium w-full sm:w-auto"
                  >
                    {config.cta_life_label} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </ApplyLink>
              </div>
              <div className="mt-2 inline-flex items-center gap-3 bg-cream border border-line px-4 py-2 rounded-sm">
                <span className="text-xs uppercase tracking-[0.2em] text-clay font-semibold" data-testid="response-label">{config.response_label}</span>
                <span className="font-heading text-lg text-forest" data-testid="response-value">{config.response_value}</span>
                <span className="text-xs text-sage" data-testid="response-suffix">{config.response_suffix}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-6">
                <span className="w-6 h-px bg-clay" />
                {config.hero_eyebrow}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] font-medium mb-6">
                {config.hero_title_prefix}{" "}
                <em className="text-clay not-italic">{config.hero_title_highlight}</em>
                {config.hero_title_suffix}
              </h1>
              <p className="text-base md:text-lg text-sage max-w-xl leading-relaxed mb-10">
                {config.hero_subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <ApplyLink to="/apply/health" applyBase={applyBase} data-testid="cta-apply-health">
                  <Button className="bg-forest hover:bg-forestdark text-bone h-12 px-6 rounded-sm font-medium w-full sm:w-auto">
                    {config.cta_health_label} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </ApplyLink>
                <ApplyLink to="/apply/life" applyBase={applyBase} data-testid="cta-apply-life">
                  <Button
                    variant="outline"
                    className="border-forest text-forest hover:bg-forest hover:text-bone h-12 px-6 rounded-sm font-medium w-full sm:w-auto"
                  >
                    {config.cta_life_label} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </ApplyLink>
              </div>
            </div>
            <div className="lg:col-span-6 relative">
              <div className="overflow-hidden rounded-md border border-line bg-cream">
                <img
                  src="https://images.unsplash.com/photo-1763357617066-8cf613ac6d32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGZhbWlseSUyMHdhbGtpbmclMjBpbiUyMG5hdHVyZSUyMHN1bnNldHxlbnwwfHx8fDE3NzgzNDM4MjR8MA&ixlib=rb-4.1.0&q=85"
                  alt="Family at sunset"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden md:block bg-bone border border-line p-5 rounded-sm w-56 shadow-sm">
                <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-2">{config.response_label}</div>
                <div className="font-heading text-3xl text-forest">{config.response_value}</div>
                <div className="text-sm text-sage mt-1">{config.response_suffix}</div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Products */}
      <section id="products" className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 items-end">
            <div className="md:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-4">Coverage</div>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">Two ways to start.</h2>
            </div>
            <p className="md:col-span-5 md:col-start-7 text-sage text-base leading-relaxed">
              Both pathways share a single guided form, attach supporting documents, and route directly
              to a licensed agent for personalised follow-up.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ApplyLink
              to="/apply/health"
              applyBase={applyBase}
              data-testid="card-health-apply"
              className="group block bg-white border border-line rounded-sm p-8 md:p-10 hover:-translate-y-[2px] hover:shadow-sm transition-transform duration-300"
            >
              <Heart className="w-8 h-8 text-clay mb-6" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl font-medium mb-3" data-testid="product-health-title">{config.product_health_title}</h3>
              <p className="text-sage leading-relaxed mb-6" data-testid="product-health-desc">
                {config.product_health_desc}
              </p>
              <ul className="text-sm text-sage space-y-2 mb-8">
                <li className="flex items-start gap-2"><span className="text-clay mt-1">—</span> In-network &amp; out-of-network options</li>
                <li className="flex items-start gap-2"><span className="text-clay mt-1">—</span> Pre-existing conditions reviewed</li>
                <li className="flex items-start gap-2"><span className="text-clay mt-1">—</span> Wellness reimbursements</li>
              </ul>
              <span className="inline-flex items-center text-forest font-medium group-hover:text-clay transition-colors duration-200">
                Start application <ArrowRight className="w-4 h-4 ml-2" />
              </span>
            </ApplyLink>
            <ApplyLink
              to="/apply/life"
              applyBase={applyBase}
              data-testid="card-life-apply"
              className="group block bg-white border border-line rounded-sm p-8 md:p-10 hover:-translate-y-[2px] hover:shadow-sm transition-transform duration-300"
            >
              <Shield className="w-8 h-8 text-clay mb-6" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl font-medium mb-3" data-testid="product-life-title">{config.product_life_title}</h3>
              <p className="text-sage leading-relaxed mb-6" data-testid="product-life-desc">
                {config.product_life_desc}
              </p>
              <ul className="text-sm text-sage space-y-2 mb-8">
                <li className="flex items-start gap-2"><span className="text-clay mt-1">—</span> 10/20/30 year term options</li>
                <li className="flex items-start gap-2"><span className="text-clay mt-1">—</span> Coverage up to $5M</li>
                <li className="flex items-start gap-2"><span className="text-clay mt-1">—</span> Multiple beneficiaries supported</li>
              </ul>
              <span className="inline-flex items-center text-forest font-medium group-hover:text-clay transition-colors duration-200">
                Start application <ArrowRight className="w-4 h-4 ml-2" />
              </span>
            </ApplyLink>
          </div>
        </div>
      </section>

      {/* Why */}
      <section id="why" className="border-b border-line bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-4">{config.why_eyebrow}</div>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">{config.why_title}</h2>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { icon: Lock, title: config.why_box_1_title, desc: config.why_box_1_desc },
              { icon: FileCheck, title: config.why_box_2_title, desc: config.why_box_2_desc },
              { icon: Users, title: config.why_box_3_title, desc: config.why_box_3_desc },
              { icon: Heart, title: config.why_box_4_title, desc: config.why_box_4_desc },
            ].map((f, i) => (
              <div key={i} className="bg-white border border-line p-6 rounded-sm" data-testid={`why-box-${i + 1}`}>
                <f.icon className="w-6 h-6 text-clay mb-4" strokeWidth={1.5} />
                <h4 className="font-heading text-lg font-medium mb-2">{f.title}</h4>
                <p className="text-sm text-sage leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-forest text-bone">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              {logoUrl ? (
                <img src={logoUrl} alt={config.brand_name} className={`${headerLogoClass} w-auto bg-bone p-1 rounded-sm`} />
              ) : (
                <div className="w-8 h-8 bg-bone rounded-sm flex items-center justify-center">
                  <Shield className="w-4 h-4 text-forest" strokeWidth={2} />
                </div>
              )}
              <span className="font-heading text-lg" data-testid="footer-brand-name">{config.brand_name}</span>
            </div>
            <p className="text-sm text-bone/70 leading-relaxed max-w-xs">
              A modern insurance application experience built for clarity, trust and speed.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-4">Contact</div>
            <ul className="space-y-3 text-sm text-bone/80">
              {config.contact_phone && (
                <li className="flex items-center gap-3" data-testid="footer-phone">
                  <Phone className="w-4 h-4 flex-shrink-0" /> {config.contact_phone}
                </li>
              )}
              {config.contact_email && (
                <li className="flex items-center gap-3 break-all" data-testid="footer-email">
                  <Mail className="w-4 h-4 flex-shrink-0" /> {config.contact_email}
                </li>
              )}
              {config.contact_address && (
                <li className="flex items-center gap-3" data-testid="footer-address">
                  <MapPin className="w-4 h-4 flex-shrink-0" /> {config.contact_address}
                </li>
              )}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-4">Quick start</div>
            <ul className="space-y-3 text-sm">
              <li><ApplyLink to="/apply/health" applyBase={applyBase} className="hover:text-clay transition-colors duration-200">Apply for Health</ApplyLink></li>
              <li><ApplyLink to="/apply/life" applyBase={applyBase} className="hover:text-clay transition-colors duration-200">Apply for Life</ApplyLink></li>
              <li><ApplyLink to="/agent/login" applyBase={applyBase} className="hover:text-clay transition-colors duration-200">Agent Portal</ApplyLink></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-bone/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 text-xs text-bone/50 flex flex-col md:flex-row justify-between gap-2">
            <span>© 2026 {config.brand_name}. All rights reserved.</span>
            <span>Licensed insurance brokerage — NPN #20109885</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
